import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, FormControl, Grid, MenuItem, OutlinedInput,
  Paper, Select, Stack, Typography,
} from '@mui/material';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';

import { SCOPE_TOKENS } from '../../../theme/muiTheme';
import { formatTonne } from '../../../shared/utils/number';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import { buildClimateActionRecommendationsUrl } from '../../climateAction/model/climateActionNavigation';
import EmissionOverviewNotice from '../../emissions/components/EmissionOverviewNotice';
import useDashboardEmissions from '../../emissions/hooks/useDashboardEmissions';
import DashboardKpiCard from './DashboardKpiCard';
import DashboardChartCard from './DashboardChartCard';
import MonthlyEmissionsChart from './MonthlyEmissionsChart';
import DistributionChart from './DistributionChart';
import YearlyInventoryChart from './YearlyInventoryChart';

const KPI_CONFIG = [
  { id: 'total', labelKey: 'dashboard.kpi.total', icon: ParkOutlinedIcon, tone: SCOPE_TOKENS.TOTAL },
  { id: 'scope1', labelKey: 'dashboard.kpi.scope1', icon: LocalFireDepartmentOutlinedIcon, tone: SCOPE_TOKENS.SCOPE_1 },
  { id: 'scope2', labelKey: 'dashboard.kpi.scope2', icon: BoltOutlinedIcon, tone: SCOPE_TOKENS.SCOPE_2 },
  { id: 'scope3', labelKey: 'dashboard.kpi.scope3', icon: PublicOutlinedIcon, tone: SCOPE_TOKENS.SCOPE_3 },
];

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: overview,
    year,
    years,
    setYear,
    loading,
    error,
    reload,
  } = useDashboardEmissions();
  const dashboard = overview.dashboard;
  const isHistorical = Boolean(overview.meta.isHistorical);
  const openScopeRecommendations = (scope) => {
    navigate(buildClimateActionRecommendationsUrl({ year, scope }));
  };
  usePagePresentation({
    loading,
    status: error ? null : overview.meta.status.dashboard,
    estimation: error ? null : overview.meta.estimatedFactors.dashboard,
  });
  const canRenderContent = !error;
  const dashboardAnomalies = overview.meta.anomalies.filter((anomaly) => {
    const code = String(anomaly).split(':')[0];
    return code !== 'MISSING_CAT5_SEAT_SNAPSHOT'
      && code !== 'MISSING_CAT5_SEAT_Q4_BASELINE'
      && code !== 'MISSING_CAT8_Q4_BASELINE';
  });

  return (
    <Box component="section" aria-labelledby="dashboard-title" className="page-stack">
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
              <Typography id="dashboard-title" component="h1" variant="h5" sx={{ fontSize: { xs: 27, md: 30 }, fontWeight: 800, lineHeight: 1.2 }}>
                {t('dashboard.title')}
              </Typography>
              <Chip
                label={t('dashboard.financialYear', { year: dashboard.year })}
                color="primary"
                size="small"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('dashboard.subtitle')}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            <FormControl size="small">
              <Select
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                input={<OutlinedInput size="small" />}
                inputProps={{ 'aria-label': t('dashboard.yearLabel') }}
                renderValue={(value) => String(value)}
                sx={{ minWidth: 132 }}
              >
                {years.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      <EmissionOverviewNotice
        error={error}
        hasData={overview.meta.hasData}
        anomalies={dashboardAnomalies}
        onRetry={reload}
      />

      {canRenderContent && (
        <>
          <Grid container spacing={2}>
            {KPI_CONFIG.map((config) => (
              <Grid item xs={12} sm={6} lg={3} key={config.id}>
                <DashboardKpiCard
                  {...config}
                  label={t(config.labelKey)}
                  value={dashboard.metrics[config.id]}
                  formattedValue={formatTonne(dashboard.metrics[config.id])}
                  previousValue={dashboard.previousMetrics?.[config.id]}
                  previousYear={dashboard.previousYear}
                  changeLabel={(value, year) => t('dashboard.kpi.changeVs', { value, year })}
                  noComparisonLabel={t('dashboard.kpi.noComparison')}
                />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} alignItems="stretch">
            {!isHistorical && <Grid item xs={12} lg={7} sx={{ display: 'flex' }}>
              <DashboardChartCard
                title={t('dashboard.chart.monthly')}
                unit={t('dashboard.chart.unit')}
                icon={StackedBarChartOutlinedIcon}
                sx={{ width: '100%' }}
              >
                <MonthlyEmissionsChart data={dashboard.monthly} />
              </DashboardChartCard>
            </Grid>}
            <Grid item xs={12} lg={isHistorical ? 12 : 5} sx={{ display: 'flex' }}>
              <DashboardChartCard
                title={t('dashboard.chart.distribution')}
                unit={t('dashboard.chart.percentOfTotal')}
                icon={GridViewOutlinedIcon}
                sx={{ width: '100%' }}
              >
                <DistributionChart
                  data={dashboard.distribution}
                  findRecommendationsLabel={t('climateAction.ai.title')}
                  onFindRecommendations={isHistorical ? undefined : openScopeRecommendations}
                />
              </DashboardChartCard>
            </Grid>
          </Grid>

          <DashboardChartCard
            title={t('dashboard.chart.yearly')}
            unit={t('dashboard.chart.unit')}
            icon={BarChartRoundedIcon}
          >
            <YearlyInventoryChart data={dashboard.yearly} />
          </DashboardChartCard>
        </>
      )}

    </Box>
  );
}
